import React, {
    Context,
    createContext,
    MutableRefObject,
    ReactNode,
    useCallback,
    useContext,
    useMemo,
    useState
} from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { DeepMap, FieldError, FieldValues, UseFormMethods } from '../../../Vendor/react-hook-form/dist/index.ie11'
import { ReactState } from '../Types/Types'
import { RESTAPIError } from '../API'
import { StatusCodes } from '../../../NonReact/constants/StatusCodes'
import { LanguageErrorCodes } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import MessageContext, { MessageContextType } from './MessageContext'
import { LanguageContext } from './index'

export type ModalUtilsContextType = {
    tabID: number
    setActiveTabID: (index: number) => void
    invalidFormSubmit: (errors: DeepMap<FieldValues, FieldError>) => void
    isValidationPopperOpen: boolean
    validationErrors: boolean[]
    serverError: Definable<string>
    isSubmitting: boolean
    failedSubmit: (error: RESTAPIError) => void
}

interface ModalUtilsProviderProps {
    children: ReactNode
    initialTabID?: number
    validationFields: string[]
    methods: UseFormMethods
    closeModal?: (refresh: boolean) => void
}

export const ModalUtilsProvider = ({
    children,
    initialTabID,
    validationFields,
    methods,
    closeModal
}: ModalUtilsProviderProps) => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    const { displayError }: MessageContextType = useContext(MessageContext)

    const [tabID, setTabID]: ReactState<number> = useState<number>(initialTabID || 0)
    const [isValidationPopperOpen, setIsValidationPopperOpen]: ReactState<boolean> = useState<boolean>(false)
    const [serverError, setServerError]: ReactState<Definable<string>> = useState<string>()
    const [isSubmitting, setIsSubmitting]: ReactState<boolean> = useState<boolean>(false)

    const {
        errors,
        formState: { isValid }
    }: UseFormMethods = methods

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const setActiveTabID = useCallback((index: number): void => {
        setTabID(index)
    }, [])

    const determineValidationErrors = useCallback(
        (formErrors: DeepMap<FieldValues, FieldError>): boolean[] =>
            validationFields.map((field: string): boolean => formErrors[field] !== undefined),
        [validationFields]
    )

    const validationErrors: boolean[] = useMemo((): boolean[] => determineValidationErrors(errors), [errors])

    const invalidFormSubmit = useCallback((): void => {
        if (!isValid) {
            const firstInvalidIndex: number = validationErrors.findIndex(
                (validationError: boolean): boolean => validationError
            )
            setActiveTabID(firstInvalidIndex)
            setIsValidationPopperOpen(true)

            setTimeout((): void => {
                if (isMounted.current) {
                    setIsValidationPopperOpen(false)
                }
            }, 2500)
        }
    }, [determineValidationErrors, setActiveTabID, validationErrors, isMounted])

    const successfulSubmit = useCallback((): void => {
        if (isMounted.current) {
            if (isVarNotEmpty(closeModal)) {
                closeModal(true)
            }
        }
    }, [isMounted])

    const failedSubmit = useCallback(
        (error: RESTAPIError): void => {
            const { errorCode, errorMessage, HTTPStatus }: RESTAPIError = error

            if (HTTPStatus === StatusCodes.OK || HTTPStatus === StatusCodes.OK_NO_CONTENT) {
                displayError(error)
                return successfulSubmit()
            }

            const errorCodeLanguage: Nullable<string> = (LanguageErrorCodes as unknown as Record<string, string>)[
                `Errorcode${errorCode}`
            ]

            setServerError(errorCodeLanguage ? language.get(errorCodeLanguage) : errorMessage)
            setIsSubmitting(false)

            return undefined
        },
        [displayError, successfulSubmit, language, setServerError]
    )

    return (
        <ModalUtilsContext.Provider
            value={{
                tabID,
                setActiveTabID,
                invalidFormSubmit,
                isValidationPopperOpen,
                validationErrors,
                serverError,
                isSubmitting,
                failedSubmit
            }}
        >
            {children}
        </ModalUtilsContext.Provider>
    )
}

export const ModalUtilsContext: Context<Definable<ModalUtilsContextType>> =
    createContext<Definable<ModalUtilsContextType>>(undefined)
